import { z } from "zod";
import { getAgeFromDateOfBirth } from "@/app/onboarding/_lib/helpers";
import {
	genderSchema,
	type OnboardingFormFieldName,
	type OnboardingFormValues,
	type OnboardingValidationErrors,
} from "@/app/onboarding/_lib/types";

export const onboardingSchema = z
	.object({
		name: z.string().trim().min(2, "Enter your full name"),
		dateOfBirth: z.string(),
		gender: genderSchema.or(z.literal("")),
		privacyConsent: z.boolean(),
	})
	.superRefine((value, ctx) => {
		if (!value.privacyConsent) {
			ctx.addIssue({
				code: "custom",
				message: "Accept the Terms and Privacy Policy to continue",
				path: ["privacyConsent"],
			});
		}

		if (!value.gender) {
			ctx.addIssue({
				code: "custom",
				message: "Select your gender",
				path: ["gender"],
			});
		}

		if (!value.dateOfBirth.trim()) {
			ctx.addIssue({
				code: "custom",
				message: "Add your date of birth",
				path: ["dateOfBirth"],
			});
			return;
		}

		const age = getAgeFromDateOfBirth(value.dateOfBirth);

		if (age === null) {
			ctx.addIssue({
				code: "custom",
				message: "Enter a valid date of birth",
				path: ["dateOfBirth"],
			});
			return;
		}

		if (age < 18) {
			ctx.addIssue({
				code: "custom",
				message: "You must be at least 18 years old to register.",
				path: ["dateOfBirth"],
			});
		}
	});

export function mapOnboardingSchemaErrors(values: OnboardingFormValues) {
	const parsed = onboardingSchema.safeParse(values);
	const errors: OnboardingValidationErrors = {};

	if (!parsed.success) {
		for (const issue of parsed.error.issues) {
			const field = issue.path[0];

			if (typeof field !== "string" || field in errors) {
				continue;
			}

			errors[field as OnboardingFormFieldName] = issue.message;
		}
	}

	return errors;
}
